import * as THREE from 'three/webgpu';

const COLORS = {
  amber: new THREE.Color(0xc78a45),
  amberDark: new THREE.Color(0x704122),
  water: new THREE.Color(0x75bedf),
  organic: new THREE.Color(0xd9b468),
  brine: new THREE.Color(0xa7d7e4),
  yellow: new THREE.Color(0xe4b72f),
  white: new THREE.Color(0xf0f1e9),
  glass: new THREE.Color(0xd8f1f4),
  dark: new THREE.Color(0x172229),
};
const STIR_START = 60;
const STIR_END = 78;
const STEPS = ['机械臂投料', '溶剂与碱', '加热搅拌', '萃取洗涤', '纯化取样'];
const clamp01 = (value) => Math.min(Math.max(value, 0), 1);
const smooth = (value) => {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
};

function boundsOf(meshes) {
  const bounds = new THREE.Box3();
  meshes.forEach((mesh) => bounds.expandByObject(mesh, true));
  return bounds;
}

function supportHeightAt(meshes, position, startY) {
  const raycaster = new THREE.Raycaster(
    new THREE.Vector3(position.x, startY, position.z),
    new THREE.Vector3(0, -1, 0),
    0,
    0.5,
  );
  meshes.forEach((mesh) => mesh.updateWorldMatrix(true, false));
  return raycaster.intersectObjects(meshes, false)[0]?.point.y;
}

function samplePath(time, points) {
  for (let index = 0; index < points.length - 1; index += 1) {
    const [startTime, start] = points[index];
    const [endTime, end] = points[index + 1];
    if (time <= endTime) {
      return start.clone().lerp(end, smooth((time - startTime) / (endTime - startTime)));
    }
  }
  return points.at(-1)[1].clone();
}

function addMesh(parent, geometry, material, name, position = [0, 0, 0]) {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.position.set(...position);
  parent.add(mesh);
  return mesh;
}

function solidMaterial(color, roughness = 0.7) {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.04 });
}

function liquidMaterial(color, opacity = 0.62) {
  return new THREE.MeshPhysicalMaterial({
    color,
    roughness: 0.14,
    transparent: true,
    opacity,
    depthWrite: false,
  });
}

function clearMaterial(opacity = 0.24) {
  return new THREE.MeshPhysicalMaterial({
    color: COLORS.glass,
    roughness: 0.08,
    metalness: 0,
    transmission: 0.76,
    thickness: 0.012,
    transparent: true,
    opacity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function configureClearGlass(material) {
  material.color?.set(0xf7fafb);
  material.transparent = true;
  material.opacity = 0.3;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;
  if ('transmission' in material) material.transmission = 0.84;
  if ('roughness' in material) material.roughness = 0.08;
  if ('metalness' in material) material.metalness = 0;
  if ('thickness' in material) material.thickness = 0.012;
  material.needsUpdate = true;
  return material;
}

function createLabel(text, width = 0.19) {
  const canvas = document.createElement('canvas');
  canvas.width = 768;
  canvas.height = 168;
  const context = canvas.getContext('2d');
  context.fillStyle = 'rgba(9, 17, 22, 0.9)';
  context.roundRect(8, 8, 752, 152, 20);
  context.fill();
  context.strokeStyle = 'rgba(87, 204, 232, 0.52)';
  context.lineWidth = 3;
  context.stroke();
  context.fillStyle = '#eefbfc';
  context.font = '600 45px Inter, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, 384, 84);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  }));
  sprite.scale.set(width, width * 0.219, 1);
  sprite.renderOrder = 30;
  return sprite;
}

function cloneAssetGroup(sources, parent, name) {
  const sourceBounds = boundsOf(sources);
  const sourceCenter = sourceBounds.getCenter(new THREE.Vector3());
  const group = new THREE.Group();
  group.name = name;
  group.position.copy(sourceCenter);
  parent.add(group);
  sources.forEach((source) => {
    source.updateMatrixWorld(true);
    const clone = source.clone();
    clone.name = `${name}_${source.name}`;
    clone.material = Array.isArray(source.material)
      ? source.material.map((material) => material.clone())
      : source.material.clone();
    const worldPosition = new THREE.Vector3();
    source.matrixWorld.decompose(worldPosition, clone.quaternion, clone.scale);
    clone.position.copy(worldPosition).sub(sourceCenter);
    if (source.name.includes('glass')) {
      const materials = Array.isArray(clone.material) ? clone.material : [clone.material];
      materials.forEach(configureClearGlass);
    }
    group.add(clone);
  });
  return { group, home: sourceCenter.clone(), size: sourceBounds.getSize(new THREE.Vector3()) };
}

function createBeaker(parent, name, radius, height, home, color) {
  const group = new THREE.Group();
  group.name = name;
  group.position.copy(home);
  parent.add(group);
  addMesh(
    group,
    new THREE.CylinderGeometry(radius, radius * 0.92, height, 24, 1, true),
    clearMaterial(0.22),
    `${name}_Glass`,
    [0, height * 0.5, 0],
  );
  const rim = addMesh(
    group,
    new THREE.TorusGeometry(radius, radius * 0.045, 8, 28),
    solidMaterial(0xc9d5d7, 0.32),
    `${name}_Rim`,
    [0, height, 0],
  );
  rim.rotation.x = Math.PI / 2;
  const liquid = addMesh(
    group,
    new THREE.CylinderGeometry(radius * 0.82, radius * 0.78, height * 0.56, 22),
    liquidMaterial(color, 0.5),
    `${name}_Liquid`,
    [0, height * 0.31, 0],
  );
  liquid.scale.y = 0;
  return { group, liquid, home: home.clone(), height };
}

function createSeparatoryFunnel(parent, radius, height, home) {
  const group = new THREE.Group();
  group.name = 'SNAr_Separatory_Funnel';
  group.position.copy(home);
  parent.add(group);
  addMesh(group, new THREE.ConeGeometry(radius, height * 0.68, 24, 1, true), clearMaterial(0.2), 'SNAr_Funnel_Glass', [0, height * 0.55, 0]);
  addMesh(group, new THREE.CylinderGeometry(radius * 0.11, radius * 0.11, height * 0.38, 12), clearMaterial(0.22), 'SNAr_Funnel_Stem', [0, height * 0.1, 0]);
  addMesh(group, new THREE.CylinderGeometry(radius * 0.24, radius * 0.24, height * 0.08, 16), solidMaterial(COLORS.dark, 0.86), 'SNAr_Funnel_Stopper', [0, height * 0.93, 0]);
  const aqueous = addMesh(group, new THREE.CylinderGeometry(radius * 0.72, radius * 0.12, height * 0.22, 22), liquidMaterial(COLORS.water), 'SNAr_Aqueous_Layer', [0, height * 0.42, 0]);
  const organic = addMesh(group, new THREE.CylinderGeometry(radius * 0.76, radius * 0.42, height * 0.2, 22), liquidMaterial(COLORS.organic), 'SNAr_Organic_Layer', [0, height * 0.64, 0]);
  aqueous.scale.y = 0;
  organic.scale.y = 0;
  return { group, aqueous, organic, home: home.clone(), height };
}

function createColumn(parent, radius, height, home) {
  const group = new THREE.Group();
  group.name = 'SNAr_Flash_Column';
  group.position.copy(home);
  parent.add(group);
  addMesh(group, new THREE.CylinderGeometry(radius, radius, height, 18), clearMaterial(0.22), 'SNAr_Column_Glass', [0, height * 0.5, 0]);
  const silica = addMesh(group, new THREE.CylinderGeometry(radius * 0.78, radius * 0.78, height * 0.58, 18), solidMaterial(0xd8ccb2, 0.92), 'SNAr_Silica', [0, height * 0.34, 0]);
  const band = addMesh(group, new THREE.CylinderGeometry(radius * 0.8, radius * 0.8, height * 0.12, 18), solidMaterial(COLORS.yellow, 0.48), 'SNAr_Product_Band', [0, height * 0.62, 0]);
  silica.scale.y = 0;
  band.scale.y = 0;
  return { group, silica, band, home: home.clone(), height };
}

function createDrops(parent, name, color, radius) {
  return Array.from({ length: 4 }, (_, index) => {
    const drop = addMesh(parent, new THREE.SphereGeometry(radius, 12, 8), liquidMaterial(color, 0.84), `${name}_${index + 1}`);
    drop.visible = false;
    return drop;
  });
}

export class SnarExperiment {
  constructor({ modelMeshes, robotArmIK }) {
    this.root = new THREE.Group();
    this.root.name = 'SNAr_Experiment_In_Existing_Lab';
    this.root.visible = false;
    this.duration = 160;
    this.elapsed = 0;
    this.playing = true;
    this.speed = 1;
    this.onStateChange = null;
    this.robotArmIK = robotArmIK;
    if (!this.robotArmIK) throw new Error('SNAr experiment requires the ABB IK rig.');

    this.sourceTube = modelMeshes.find((mesh) => mesh.name === 'Reactiontube_1mL');
    this.shelfMeshes = modelMeshes.filter((mesh) => mesh.name.startsWith('Main_Shelves_001'));
    this.powderMeshes = modelMeshes.filter((mesh) => mesh.name.startsWith('Powder_Adding_Module'));
    this.mixingMeshes = modelMeshes.filter((mesh) => mesh.name.startsWith('Mixing_Module'));
    this.staticArmMeshes = modelMeshes.filter((mesh) => mesh.name.startsWith('机械臂'));
    const dmpuSources = modelMeshes.filter((mesh) => mesh.name.startsWith('Bottle_8ml_002__'));
    const etoacSources = modelMeshes.filter((mesh) => mesh.name.startsWith('Bottle_8ml_003__'));
    const waterSources = modelMeshes.filter((mesh) => mesh.name.startsWith('Bottle_125ml_002__'));
    const brineSources = modelMeshes.filter((mesh) => mesh.name.startsWith('Bottle_125ml_002_001__'));
    const assetPrefixes = ['Desktop', 'Main_Shelves_001', 'Powder_Adding_Module', 'Mixing_Module', 'Sharp_Fusion_1_003', 'Sharp_Fusion_1_013_001_001'];
    this.assetMeshes = modelMeshes.filter(
      (mesh) => mesh !== this.sourceTube && assetPrefixes.some((prefix) => mesh.name.startsWith(prefix)),
    );
    if (!this.sourceTube || !this.shelfMeshes.length || !this.powderMeshes.length
      || !this.mixingMeshes.length || !this.staticArmMeshes.length || !dmpuSources.length
      || !etoacSources.length || !waterSources.length || !brineSources.length) {
      throw new Error('SNAr experiment assets are incomplete in labgym_web.glb.');
    }

    const tubeBounds = boundsOf([this.sourceTube]);
    const tubeSize = tubeBounds.getSize(new THREE.Vector3());
    const tubeSourceCenter = tubeBounds.getCenter(new THREE.Vector3());
    this.tubeHeight = tubeSize.y;
    const shelfBounds = boundsOf(this.shelfMeshes);
    const tubeSupportY = supportHeightAt(this.shelfMeshes, tubeSourceCenter, tubeBounds.min.y + 0.02);
    if (tubeSupportY === undefined) throw new Error('Could not find the SNAr shelf surface.');
    this.shelfSurfaceY = tubeSupportY + 0.001;
    this.homePosition = tubeSourceCenter.clone().setY(
      this.shelfSurfaceY + Math.max(this.tubeHeight * 0.07, 0.006) + this.tubeHeight * 0.5,
    );

    this.tube = this.sourceTube.clone();
    this.tube.name = 'SNAr_Reaction_Vessel';
    this.tube.material = Array.isArray(this.sourceTube.material)
      ? this.sourceTube.material.map((material) => configureClearGlass(material.clone()))
      : configureClearGlass(this.sourceTube.material.clone());
    this.sourceTube.getWorldPosition(this.tube.position);
    this.sourceTube.getWorldQuaternion(this.tube.quaternion);
    this.sourceTube.getWorldScale(this.tube.scale);
    this.tube.position.add(this.homePosition.clone().sub(tubeSourceCenter));
    this.tubeInitialPosition = this.tube.position.clone();
    this.root.add(this.tube);

    this.dmpu = cloneAssetGroup(dmpuSources, this.root, 'SNAr_DMPU_50mL');
    this.etoac = cloneAssetGroup(etoacSources, this.root, 'SNAr_EtOAc_150mL');
    this.waterBottle = cloneAssetGroup(waterSources, this.root, 'SNAr_Water_200mL');
    this.brineBottle = cloneAssetGroup(brineSources, this.root, 'SNAr_Brine_100mL');

    const powderBounds = boundsOf(this.powderMeshes);
    const powderSize = powderBounds.getSize(new THREE.Vector3());
    const powderCenter = powderBounds.getCenter(new THREE.Vector3());
    const openingX = powderCenter.x + powderSize.x * 0.26;
    const openingY = powderBounds.min.y + powderSize.y * 0.3;
    const frontZ = powderBounds.max.z;
    this.powderTarget = new THREE.Vector3(openingX, openingY, frontZ - this.tubeHeight * 0.35);
    this.powderEntrance = new THREE.Vector3(openingX, openingY, frontZ + this.tubeHeight * 0.45);
    this.powderApproach = this.powderEntrance.clone().add(new THREE.Vector3(0, 0, Math.max(powderSize.z * 0.13, this.tubeHeight * 3.5)));
    const transferY = this.homePosition.y + this.tubeHeight * 2.2;
    this.homeLift = this.homePosition.clone().setY(transferY);
    this.powderHighApproach = this.powderApproach.clone().setY(transferY);
    const shelfExitZ = shelfBounds.min.z - Math.max(this.tubeHeight * 2.4, 0.07);
    this.shelfExit = this.homeLift.clone().setZ(shelfExitZ);

    const mixingBounds = boundsOf(this.mixingMeshes);
    const mixingCenter = mixingBounds.getCenter(new THREE.Vector3());
    this.mixingTarget = new THREE.Vector3(mixingCenter.x, mixingBounds.max.y + this.tubeHeight * 0.52 + 0.004, mixingCenter.z);
    this.mixingApproach = this.mixingTarget.clone().add(new THREE.Vector3(0, this.tubeHeight * 1.75, this.tubeHeight * 1.4));

    const shelfWidth = shelfBounds.getSize(new THREE.Vector3()).x;
    const spacing = Math.min(Math.max(this.tubeHeight * 0.82, 0.048), shelfWidth * 0.095);
    const shelfZ = this.homePosition.z;
    const baseY = this.shelfSurfaceY + 0.003;
    // The authored bottle instances are not all on the same shelf row. Re-stage
    // every cloned source explicitly so seeking never reveals a floating bottle.
    const stageBottle = (asset, x, z = shelfZ) => {
      asset.home.set(x, this.shelfSurfaceY + asset.size.y * 0.5 + 0.006, z);
      asset.group.position.copy(asset.home);
    };
    stageBottle(this.dmpu, this.homePosition.x - spacing * 2.2);
    stageBottle(this.etoac, this.homePosition.x - spacing * 0.9);
    stageBottle(this.waterBottle, this.homePosition.x + spacing * 0.9);
    stageBottle(this.brineBottle, this.homePosition.x + spacing * 2.25);
    this.quench = createBeaker(this.root, 'SNAr_Quench_Beaker', this.tubeHeight * 0.32, this.tubeHeight * 0.94, new THREE.Vector3(this.homePosition.x + spacing * 1.35, baseY, shelfZ), COLORS.water);
    this.funnel = createSeparatoryFunnel(this.root, this.tubeHeight * 0.32, this.tubeHeight * 1.28, new THREE.Vector3(this.homePosition.x + spacing * 2.65, baseY, shelfZ));
    this.drying = createBeaker(this.root, 'SNAr_Drying_Flask', this.tubeHeight * 0.28, this.tubeHeight * 0.82, new THREE.Vector3(this.homePosition.x - spacing * 1.35, baseY, shelfZ), COLORS.organic);
    this.column = createColumn(this.root, this.tubeHeight * 0.17, this.tubeHeight * 1.34, new THREE.Vector3(this.homePosition.x - spacing * 2.55, baseY, shelfZ));

    this.productVial = new THREE.Group();
    this.productVial.name = 'SNAr_Product_Vial';
    this.productVialHome = new THREE.Vector3(this.homePosition.x - spacing * 3.25, baseY, shelfZ);
    this.productVial.position.copy(this.productVialHome);
    this.root.add(this.productVial);
    addMesh(this.productVial, new THREE.CylinderGeometry(this.tubeHeight * 0.12, this.tubeHeight * 0.13, this.tubeHeight * 0.52, 18), clearMaterial(0.27), 'SNAr_Product_Vial_Glass', [0, this.tubeHeight * 0.27, 0]);
    this.productOil = addMesh(this.productVial, new THREE.CylinderGeometry(this.tubeHeight * 0.09, this.tubeHeight * 0.1, this.tubeHeight * 0.21, 16), liquidMaterial(COLORS.yellow, 0.76), 'SNAr_Yellow_Oil_0_7g', [0, this.tubeHeight * 0.12, 0]);
    this.productOil.scale.y = 0;

    this.stirBarHome = new THREE.Vector3(this.homePosition.x + spacing * 3.7, baseY + this.tubeHeight * 0.055, shelfZ);
    this.stirBar = addMesh(this.root, new THREE.CapsuleGeometry(this.tubeHeight * 0.035, this.tubeHeight * 0.22, 4, 10), solidMaterial(0xe4e7e3, 0.38), 'SNAr_Magnetic_Stir_Bar');
    this.stirBar.position.copy(this.stirBarHome);

    this.tubeEffects = new THREE.Group();
    this.tubeEffects.name = 'SNAr_Reaction_Vessel_Effects';
    this.root.add(this.tubeEffects);
    this.reactionLiquid = addMesh(this.tubeEffects, new THREE.CylinderGeometry(tubeSize.x * 0.31, tubeSize.x * 0.27, this.tubeHeight * 0.48, 20), liquidMaterial(COLORS.amber, 0.65), 'SNAr_Reaction_Mixture', [0, -this.tubeHeight * 0.18, 0]);
    this.reactionLiquid.scale.y = 0;
    this.powderGrains = Array.from({ length: 15 }, (_, index) => {
      const angle = index * 2.39996;
      const radius = tubeSize.x * (0.06 + (index % 3) * 0.065);
      const grain = addMesh(this.tubeEffects, new THREE.DodecahedronGeometry(tubeSize.x * (0.042 + (index % 2) * 0.01), 0), solidMaterial(index >= 10 ? 0xe6e2d5 : COLORS.white, 0.9), `SNAr_Solid_${index + 1}`, [Math.cos(angle) * radius, -this.tubeHeight * (0.34 - (index % 5) * 0.028), Math.sin(angle) * radius]);
      grain.scale.setScalar(0);
      return grain;
    });
    this.dryingPowder = Array.from({ length: 10 }, (_, index) => {
      const grain = addMesh(this.drying.group, new THREE.DodecahedronGeometry(this.tubeHeight * 0.025, 0), solidMaterial(0xdad7cc, 0.94), `SNAr_Na2SO4_${index + 1}`, [((index % 4) - 1.5) * this.tubeHeight * 0.045, this.tubeHeight * (0.08 + Math.floor(index / 4) * 0.035), ((index % 3) - 1) * this.tubeHeight * 0.04]);
      grain.scale.setScalar(0);
      return grain;
    });

    this.heatRing = addMesh(this.root, new THREE.TorusGeometry(this.tubeHeight * 0.27, this.tubeHeight * 0.025, 8, 28), new THREE.MeshBasicMaterial({ color: 0xf0a12f, transparent: true, opacity: 0 }), 'SNAr_100C_Heat_Ring');
    this.heatRing.rotation.x = Math.PI / 2;

    this.labels = {
      tube: createLabel('SNAr 反应管'),
      dmpu: createLabel('无水 DMPU 50 mL'),
      etoac: createLabel('EtOAc 3 × 150 mL'),
      water: createLabel('水 200 mL / 水洗'),
      brine: createLabel('饱和 NaCl 2 × 100 mL'),
      stir: createLabel('磁力转子', 0.14),
      product: createLabel('黄色油 0.7 g · 9% · CI-MS m/z 201', 0.29),
    };
    Object.values(this.labels).forEach((label) => this.root.add(label));
    const dropRadius = Math.max(tubeSize.x * 0.22, 0.0026);
    this.solventDrops = createDrops(this.root, 'SNAr_DMPU_Drop', 0xc7dbe1, dropRadius);
    this.quenchDrops = createDrops(this.root, 'SNAr_Quench_Drop', COLORS.amber, dropRadius);
    this.extractionDrops = createDrops(this.root, 'SNAr_EtOAc_Drop', COLORS.organic, dropRadius);
    this.washDrops = createDrops(this.root, 'SNAr_Wash_Drop', COLORS.water, dropRadius);

    this.tubePowderPaths = [this.createPowderTrip(0), this.createPowderTrip(10), this.createPowderTrip(30)];
    this.tubeToMixPath = [
      [50, this.homePosition], [51, this.homeLift], [52, this.shelfExit],
      [54.5, this.mixingApproach], [57, this.mixingTarget.clone().setY(this.mixingApproach.y)],
      [59, this.mixingTarget], [60, this.mixingTarget],
    ];
    const processClear = this.mixingApproach.clone().setY(transferY);
    this.tubeQuenchPath = [
      [92, this.mixingTarget], [93, this.mixingApproach], [95, processClear],
      [97, this.quench.home.clone().add(new THREE.Vector3(0, this.quench.height + this.tubeHeight * 0.85, 0))],
      [100, this.quench.home.clone().add(new THREE.Vector3(0, this.quench.height + this.tubeHeight * 0.85, 0))],
    ];

    this.robotArmIK.configureExternalAlignment(boundsOf(this.staticArmMeshes));
    this.armTubeOffset = new THREE.Vector3(0, this.tubeHeight * 0.58, 0);
    const armBase = this.robotArmIK.getExternalBaseWorld();
    const rackDirection = this.homePosition.clone().sub(armBase).setY(0);
    // Park part-way between the base and rack. A target directly above the
    // far shelf puts J2/J3 close to a straight-arm singularity.
    this.armPark = armBase.clone().addScaledVector(rackDirection, 0.58);
    this.armPark.y = Math.max(
      this.homePosition.y + this.tubeHeight * 1.25,
      armBase.y + this.tubeHeight * 2.5,
    );
    this.armEndTarget = this.armPark.clone();
    this.effectMeshes = [];
    this.root.traverse((object) => {
      if (object.isMesh) this.effectMeshes.push(object);
    });
    this.meshes = this.assetMeshes.concat(this.effectMeshes, this.robotArmIK.meshes);
    this.applyState();
  }

  createPowderTrip(offset) {
    return [
      [offset, this.homePosition], [offset + 0.8, this.homeLift], [offset + 1.5, this.shelfExit],
      [offset + 2.4, this.powderHighApproach], [offset + 3.1, this.powderApproach],
      [offset + 3.7, this.powderEntrance], [offset + 4.2, this.powderTarget],
      [offset + 6.1, this.powderTarget], [offset + 6.6, this.powderEntrance],
      [offset + 7.2, this.powderApproach], [offset + 8, this.powderHighApproach],
      [offset + 8.7, this.shelfExit], [offset + 9.4, this.homeLift],
      [offset + 10, this.homePosition],
    ];
  }

  get phase() {
    const time = this.elapsed;
    if (time < 10) return { index: 0, label: '机械臂加入 4-氨基苯酚', detail: '搬运反应管进出加粉站，加入 3.6 g（32.8 mmol）', steps: STEPS };
    if (time < 20) return { index: 0, label: '机械臂加入 4-氯皮考啉', detail: '反应管再次进入加料站，加入 5.0 g（39.3 mmol）', steps: STEPS };
    if (time < 30) return { index: 1, label: '机械臂定量加入无水 DMPU', detail: '从货架取瓶，向直立反应管加入 50 mL 后归位', steps: STEPS };
    if (time < 40) return { index: 1, label: '机械臂加入叔丁醇钾', detail: '反应管第三次进出加粉站，一次加入 7.4 g（65.6 mmol）', steps: STEPS };
    if (time < 50) return { index: 1, label: '机械臂安装磁力转子', detail: '从货架夹取转子并垂直放入反应管', steps: STEPS };
    if (time < 60) return { index: 2, label: '转运至混合模块', detail: '反应管保持直立，机械臂放置到现有 Mixing Module', steps: STEPS };
    if (time < STIR_END) return { index: 2, label: '100°C 搅拌反应 · 18 h', detail: this.speed === 100 ? '100× 加速中：仅加速本搅拌阶段' : '点击 100× 可加速 18 h 搅拌阶段', steps: STEPS };
    if (time < 84) return { index: 2, label: '冷却至室温', detail: '停止加热，反应管仍保持直立', steps: STEPS };
    if (time < 92) return { index: 2, label: '机械臂移除磁力转子', detail: '垂直取出转子并放回货架槽位', steps: STEPS };
    if (time < 100) return { index: 3, label: '机械臂转移并水淬灭', detail: '将反应液定量转入水 200 mL 淬灭烧杯', steps: STEPS };
    if (time < 108) return { index: 3, label: '转入分液器皿', detail: '机械臂搬运淬灭烧杯，将混合液转入分液器皿', steps: STEPS };
    if (time < 118) return { index: 3, label: 'EtOAc 萃取 · 3 次', detail: '机械臂加入 150 mL × 3；上层为 EtOAc 有机相', steps: STEPS };
    if (time < 126) return { index: 3, label: '分层并合并有机相', detail: '轻柔混合、静置分层，排出下层水相并保留上层', steps: STEPS };
    if (time < 134) return { index: 3, label: '水洗与饱和盐水洗', detail: '依次水洗 3 × 100 mL、饱和 NaCl 洗 2 × 100 mL', steps: STEPS };
    if (time < 142) return { index: 3, label: 'Na₂SO₄ 干燥', detail: '机械臂将有机相容器送至加粉站，加入无水硫酸钠', steps: STEPS };
    if (time < 150) return { index: 4, label: '过滤并减压浓缩', detail: '机械臂转移干燥后的有机相至混合模块浓缩位', steps: STEPS };
    if (time < 157) return { index: 4, label: '闪式柱层析与取样', detail: 'EtOAc/己烷 = 50/50；机械臂收集黄色馏分并取样', steps: STEPS };
    return { index: 4, label: '产物确认', detail: '黄色油 0.7 g，9%；CI-MS m/z 201（M+H）+', steps: STEPS };
  }

  setVisible(visible) {
    this.root.visible = visible;
    if (visible) this.sourceTube.visible = false;
    if (!visible) {
      this.sourceTube.visible = true;
      this.restoreTube();
    }
  }

  setPlaying(playing) {
    if (playing && this.elapsed >= this.duration) this.elapsed = 0;
    this.playing = playing;
    this.onStateChange?.();
  }

  toggle() { this.setPlaying(!this.playing); }
  toggleSpeed() {
    this.speed = this.speed === 1 ? 100 : 1;
    this.onStateChange?.();
  }

  reset() {
    this.elapsed = 0;
    this.playing = true;
    this.speed = 1;
    this.robotArmIK.reset();
    this.applyState();
    this.onStateChange?.();
  }

  seek(time) {
    this.elapsed = THREE.MathUtils.clamp(time, 0, this.duration);
    this.playing = false;
    this.speed = 1;
    this.robotArmIK.reset();
    this.applyState();
    this.onStateChange?.();
  }

  update(delta) {
    if (!this.playing || !this.root.visible) return;
    const frameDelta = Math.min(delta, 0.05);
    if (this.elapsed >= STIR_START && this.elapsed < STIR_END && this.speed === 100) {
      this.elapsed = Math.min(this.elapsed + frameDelta * 100, STIR_END);
      if (this.elapsed >= STIR_END) this.speed = 1;
    } else {
      this.elapsed = Math.min(this.elapsed + frameDelta, this.duration);
    }
    if (this.elapsed >= this.duration) this.playing = false;
    this.applyState();
    this.onStateChange?.();
  }

  moveTubeTo(worldCenter) {
    this.tube.position.copy(this.tubeInitialPosition).add(worldCenter.clone().sub(this.homePosition));
    this.tube.updateMatrixWorld(true);
    this.tubeEffects.position.copy(worldCenter);
  }

  restoreTube() {
    this.tube.position.copy(this.tubeInitialPosition);
    this.tube.updateMatrixWorld(true);
  }

  moveDrops(drops, start, end, startTime, endTime) {
    drops.forEach((drop, index) => {
      const progress = clamp01((this.elapsed - startTime) / (endTime - startTime) * 1.55 - index * 0.18);
      drop.visible = this.elapsed >= startTime && this.elapsed <= endTime && progress > 0.015 && progress < 0.985;
      drop.position.lerpVectors(start, end, progress);
    });
  }

  bottlePath(asset, startTime, target, holdStart, holdEnd) {
    const lift = asset.home.clone().add(new THREE.Vector3(0, asset.size.y * 0.8, 0));
    const targetLift = target.clone().add(new THREE.Vector3(0, asset.size.y * 0.45, 0));
    return [
      [startTime, asset.home], [startTime + 2, asset.home], [startTime + 3, lift],
      [holdStart, targetLift], [holdStart + 0.5, target], [holdEnd, target],
      [holdEnd + 0.5, targetLift], [startTime + 9, lift], [startTime + 10, asset.home],
    ];
  }

  applyState() {
    const time = this.elapsed;
    let tubePosition = this.homePosition.clone();
    if (time < 10) tubePosition = samplePath(time, this.tubePowderPaths[0]);
    else if (time < 20) tubePosition = samplePath(time, this.tubePowderPaths[1]);
    else if (time < 30) tubePosition = this.homePosition.clone();
    else if (time < 40) tubePosition = samplePath(time, this.tubePowderPaths[2]);
    else if (time < 50) tubePosition = this.homePosition.clone();
    else if (time < 92) tubePosition = time < 60 ? samplePath(time, this.tubeToMixPath) : this.mixingTarget.clone();
    else tubePosition = time < 100 ? samplePath(time, this.tubeQuenchPath) : this.tubeQuenchPath.at(-1)[1].clone();
    this.moveTubeTo(tubePosition);

    const dmpuTarget = this.homePosition.clone().add(new THREE.Vector3(0, this.tubeHeight * 1.35, 0));
    this.dmpu.group.position.copy(samplePath(time, this.bottlePath(this.dmpu, 20, dmpuTarget, 24, 27)));
    const funnelMouth = this.funnel.home.clone().add(new THREE.Vector3(0, this.funnel.height * 1.08, 0));
    this.etoac.group.position.copy(samplePath(time, this.bottlePath(this.etoac, 108, funnelMouth, 111, 115.5)));
    this.waterBottle.group.position.copy(samplePath(time, this.bottlePath(this.waterBottle, 124, funnelMouth, 127, 129)));
    this.brineBottle.group.position.copy(samplePath(time, this.bottlePath(this.brineBottle, 124, funnelMouth, 130.5, 133)));

    const stirInsertPath = [
      [40, this.stirBarHome], [42, this.stirBarHome],
      [44, this.stirBarHome.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.7, 0))],
      [47, this.homePosition.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.62, 0))],
      [49, this.homePosition.clone().add(new THREE.Vector3(0, -this.tubeHeight * 0.34, 0))],
      [50, this.homePosition.clone().add(new THREE.Vector3(0, -this.tubeHeight * 0.34, 0))],
    ];
    const stirRemovePath = [
      [84, this.mixingTarget.clone().add(new THREE.Vector3(0, -this.tubeHeight * 0.34, 0))],
      [86, this.mixingTarget.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.75, 0))],
      [89, this.stirBarHome.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.7, 0))],
      [91, this.stirBarHome], [92, this.stirBarHome],
    ];
    if (time < 50) this.stirBar.position.copy(samplePath(time, stirInsertPath));
    else if (time < 84) this.stirBar.position.copy(tubePosition).add(new THREE.Vector3(0, -this.tubeHeight * 0.34, 0));
    else this.stirBar.position.copy(samplePath(time, stirRemovePath));
    this.stirBar.rotation.set(0, time >= STIR_START && time < STIR_END ? time * 17 : 0, Math.PI / 2);

    const quenchWork = this.funnel.home.clone().add(new THREE.Vector3(-this.tubeHeight * 1.15, 0, this.tubeHeight * 1.25));
    const quenchPath = [
      [100, this.quench.home], [102, this.quench.home.clone().add(new THREE.Vector3(0, this.tubeHeight, 0))],
      [104, quenchWork.clone().add(new THREE.Vector3(0, this.tubeHeight, 0))], [106, quenchWork],
      [108, this.quench.home],
    ];
    this.quench.group.position.copy(samplePath(time, quenchPath));

    const funnelLift = this.funnel.home.clone().add(new THREE.Vector3(0, this.tubeHeight * 1.1, 0));
    this.funnel.group.position.copy(samplePath(time, [[118, this.funnel.home], [120, funnelLift], [124, funnelLift], [126, this.funnel.home]]));
    if (time >= 120 && time < 124) {
      this.funnel.group.position.x += Math.sin(time * 5.2) * this.tubeHeight * 0.08;
      this.funnel.group.position.z += Math.cos(time * 4.6) * this.tubeHeight * 0.06;
    }

    const dryingPowderPath = [
      [134, this.drying.home], [135, this.drying.home.clone().add(new THREE.Vector3(0, this.tubeHeight, 0))],
      [137, this.powderHighApproach.clone().setY(this.drying.home.y + this.tubeHeight)],
      [138, this.powderApproach], [139, this.powderTarget], [140, this.powderApproach],
      [141, this.drying.home.clone().add(new THREE.Vector3(0, this.tubeHeight, 0))], [142, this.drying.home],
    ];
    this.drying.group.position.copy(samplePath(time, dryingPowderPath));
    const dryingConcentratePath = [
      [142, this.drying.home], [144, this.drying.home.clone().add(new THREE.Vector3(0, this.tubeHeight, 0))],
      [147, this.mixingApproach.clone().setY(this.drying.home.y + this.tubeHeight)],
      [149, this.mixingTarget.clone().setY(this.mixingTarget.y - this.tubeHeight * 0.42)],
      [150, this.mixingTarget.clone().setY(this.mixingTarget.y - this.tubeHeight * 0.42)],
    ];
    if (time >= 142) this.drying.group.position.copy(samplePath(time, dryingConcentratePath));

    const vialCollect = this.column.home.clone().add(new THREE.Vector3(0, 0, this.tubeHeight * 0.5));
    const vialPath = [
      [150, this.productVialHome], [152, this.productVialHome.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.8, 0))],
      [154, vialCollect.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.3, 0))],
      [156, vialCollect], [158, this.productVialHome],
    ];
    this.productVial.position.copy(samplePath(time, vialPath));

    let armTarget = this.armPark.clone();
    let wristRoll = 0;
    let joint4Angle = null;
    if (time < 20 || (time >= 30 && time < 40) || (time >= 50 && time < 60) || (time >= 92 && time < 100)) {
      armTarget = tubePosition.clone().add(this.armTubeOffset);
      wristRoll = Math.sin(time * 0.7) * 0.18;
    } else if (time >= 20 && time < 30) {
      armTarget = this.dmpu.group.position.clone().add(new THREE.Vector3(0, this.dmpu.size.y * 0.32, 0));
      joint4Angle = Math.PI * 0.9;
    }
    else if ((time >= 40 && time < 50) || (time >= 84 && time < 92)) armTarget = this.stirBar.position.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.42, 0));
    else if (time >= 100 && time < 108) armTarget = this.quench.group.position.clone().add(new THREE.Vector3(0, this.quench.height * 0.72, 0));
    else if (time >= 108 && time < 118) {
      armTarget = this.etoac.group.position.clone().add(new THREE.Vector3(0, this.etoac.size.y * 0.32, 0));
      joint4Angle = Math.PI * 0.9;
    }
    else if (time >= 118 && time < 126) armTarget = this.funnel.group.position.clone().add(new THREE.Vector3(0, this.funnel.height * 0.58, 0));
    else if (time >= 126 && time < 130.5) {
      armTarget = this.waterBottle.group.position.clone().add(new THREE.Vector3(0, this.waterBottle.size.y * 0.32, 0));
      joint4Angle = Math.PI * 0.9;
    }
    else if (time >= 130.5 && time < 134) {
      armTarget = this.brineBottle.group.position.clone().add(new THREE.Vector3(0, this.brineBottle.size.y * 0.32, 0));
      joint4Angle = Math.PI * 0.9;
    }
    else if (time >= 134 && time < 150) armTarget = this.drying.group.position.clone().add(new THREE.Vector3(0, this.drying.height * 0.72, 0));
    else if (time >= 150 && time < 158) armTarget = this.productVial.position.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.48, 0));
    else if (time >= 158) {
      const vialRelease = this.productVialHome.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.48, 0));
      armTarget = samplePath(time, [[158, vialRelease], [160, this.armEndTarget]]);
    }
    this.robotArmIK.setWorldTarget(armTarget, wristRoll, joint4Angle);

    const additions = [smooth((time - 4.2) / 1.8), smooth((time - 14.2) / 1.8), smooth((time - 34.2) / 1.8)];
    this.powderGrains.forEach((grain, index) => grain.scale.setScalar(additions[Math.floor(index / 5)] * (0.75 + (index % 3) * 0.08)));
    const solventAdded = smooth((time - 24.3) / 2.2);
    const reacting = smooth((time - STIR_START) / (STIR_END - STIR_START));
    this.reactionLiquid.scale.y = solventAdded;
    this.reactionLiquid.material.color.copy(COLORS.amber).lerp(COLORS.amberDark, reacting * 0.46);
    this.heatRing.position.copy(this.mixingTarget).setY(this.mixingTarget.y - this.tubeHeight * 0.48);
    this.heatRing.material.opacity = time >= STIR_START && time < STIR_END ? 0.7 + Math.sin(time * 7) * 0.14 : 0;
    this.quench.liquid.scale.y = smooth((time - 96.5) / 3.2);
    this.funnel.aqueous.scale.y = smooth((time - 102) / 5);
    this.funnel.organic.scale.y = smooth((time - 109) / 5);
    this.funnel.organic.material.color.copy(COLORS.organic).lerp(COLORS.yellow, smooth((time - 112) / 14) * 0.35);
    this.funnel.aqueous.material.color.copy(COLORS.water).lerp(COLORS.brine, smooth((time - 126) / 8));
    this.drying.liquid.scale.y = smooth((time - 123) / 8) * (1 - smooth((time - 144) / 6));
    this.dryingPowder.forEach((grain, index) => grain.scale.setScalar(smooth((time - 138.2) / 1.2 - index * 0.025)));
    this.column.silica.scale.y = smooth((time - 148) / 4);
    this.column.band.scale.y = smooth((time - 151) / 4);
    this.productOil.scale.y = smooth((time - 154) / 3);

    this.moveDrops(this.solventDrops, dmpuTarget.clone().add(new THREE.Vector3(0, -this.dmpu.size.y * 0.45, 0)), this.homePosition.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.35, 0)), 24.2, 27.2);
    this.moveDrops(this.quenchDrops, tubePosition.clone().add(new THREE.Vector3(0, -this.tubeHeight * 0.45, 0)), this.quench.home.clone().add(new THREE.Vector3(0, this.quench.height * 0.75, 0)), 97, 100);
    this.moveDrops(this.extractionDrops, funnelMouth.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.55, 0)), funnelMouth, 111, 115.5);
    this.moveDrops(this.washDrops, funnelMouth.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.55, 0)), funnelMouth, 127, 133);

    this.labels.tube.position.copy(tubePosition).add(new THREE.Vector3(0, this.tubeHeight * 1.28, 0));
    this.labels.dmpu.position.copy(this.dmpu.group.position).add(new THREE.Vector3(0, this.dmpu.size.y * 0.9, 0));
    this.labels.etoac.position.copy(this.etoac.group.position).add(new THREE.Vector3(0, this.etoac.size.y * 0.9, 0));
    this.labels.water.position.copy(this.waterBottle.group.position).add(new THREE.Vector3(0, this.waterBottle.size.y * 0.8, 0));
    this.labels.brine.position.copy(this.brineBottle.group.position).add(new THREE.Vector3(0, this.brineBottle.size.y * 0.8, 0));
    this.labels.stir.position.copy(this.stirBar.position).add(new THREE.Vector3(0, this.tubeHeight * 0.5, 0));
    this.labels.product.position.copy(this.productVial.position).add(new THREE.Vector3(0, this.tubeHeight * 1.05, 0));
    this.labels.tube.visible = time < 100;
    this.labels.dmpu.visible = time >= 20 && time < 30;
    this.labels.etoac.visible = time >= 108 && time < 118;
    this.labels.water.visible = time >= 92 && time < 130.5;
    this.labels.brine.visible = time >= 130.5 && time < 134;
    this.labels.stir.visible = (time >= 40 && time < 50) || (time >= 84 && time < 92);
    this.labels.product.visible = time >= 157;
  }

  setWireframe(enabled) {
    this.effectMeshes.forEach((mesh) => {
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach((material) => {
        material.wireframe = enabled;
        material.needsUpdate = true;
      });
    });
  }
}
